import { isListingBinding, isListingFieldBinding, listingBindingValue, urlButtonParamName } from "../../../../@types/sharedSchema";
import type { ListingBindingSource, ProcessedListingData, REST_schema, WhatsappTemplateParam } from "../../../../@types/sharedSchema";
import { buildWatermarkFreeUrlMap } from "../../../../utils/listings/watermarkFree";

/**
 * A template that is ABOUT a listing (2026-10-21). PURE — no React, no requests.
 *
 * Haim: "why are we proposing a dummy image? why can't I actually pick a listing?" The starter
 * templates bound their fields to `listing.*` from the start, and nothing ever filled them: every
 * send went out with "-" for the rooms and the price, under the approval sample's stock photo.
 * Now every dialog that sends a template asks for the listing when the template reads one, fills
 * the fields from it (editable — they ride as the send's static values), and offers the listing's
 * own pictures for the header. The formatter is the shared schema's `listingBindingValue`, so the
 * preview and the message agree.
 *
 * The LINK is not filled here, on purpose. It is each recipient's own minisite page for the listing
 * (Haim: "we don't want to send a link to Yad2 or Madlan"), so only the server can know it: it puts the
 * listing on every recipient's minisite — creating the minisite when there is none — at campaign create.
 */

type TemplateView = REST_schema.WhatsappTemplateView;

/** The listing a send is about: the picker's search hit, topped up with the stored document. */
export type PickedListing = Partial<ProcessedListingData> & { id: string };

/** The dialog needs a listing for this template: a field reads one, or the link (each recipient's minisite page). */
export const templateHasListingParams = (template: Pick<TemplateView, "params">): boolean =>
  (template.params ?? []).some((param) => isListingBinding(param.binding));

/** The dialog ASKS for a listing: a param reads one, or the header is declared the listing's photo. */
export const templateWantsListing = (template: Pick<TemplateView, "params" | "headerMediaSource">): boolean =>
  templateHasListingParams(template) || template.headerMediaSource === "listing";

/**
 * The send cannot go WITHOUT a listing: only the listing's LINK, which is each recipient's own minisite
 * page and is made from the picked listing at the send. Everything else a listing fills can be typed —
 * its fields are ordinary text the realtor may write themselves, and a picture can be uploaded — so a
 * template that only READS a listing's city never makes the realtor pick one (Haim, 2026-10-25: "i
 * dont want to have to pick the listing just to fill the city... weird UX").
 */
export const templateRequiresListing = (template: Pick<TemplateView, "params">): boolean =>
  (template.params ?? []).some((param) => param.binding === "listing.link");

/**
 * The send must carry a picture of its own: an IMAGE header's approved file is only Meta's review
 * sample (Haim: "why do you always include this default image?"), so a picture of the listing or an
 * upload is chosen for every send, and the dialog does not send without one. A VIDEO header keeps its
 * own file — a listing has no video to offer instead.
 */
export const templateNeedsPicture = (template: Pick<TemplateView, "headerFormat">): boolean => template.headerFormat === "IMAGE";

/**
 * A URL button that opens THE LISTING the send is about: its address ends in the `listing.link` variable,
 * which the server fills per client with the listing's page in that client's minisite. Every other URL
 * button is fixed in the approved template and opens the same page for every send — whatever listing was
 * picked (Haim's test, 2026-10-22: a fixed button still opened an old listing of Tal's, long gone).
 */
export const LISTING_BUTTON_PREFIX = "https://app.webot.co.il/";
export const LISTING_LINK_EXAMPLE = "myListings/AbCd1234/EfGh5678";

// The button's variable is read through `urlButtonParamName`: a button synced back from Meta can hold
// its variable only in the address (`…/%7B%7Blisting_path%7D%7D`), and read off `paramName` alone the
// starter pack's "נכס חדש" was called a fixed address in every send dialog (Haim, 2026-10-25).
export const opensPickedListing = (
  button: { type: string; url?: string; paramName?: string },
  params: readonly Pick<WhatsappTemplateParam, "paramName" | "binding">[]
): boolean => {
  const paramName = urlButtonParamName(button);
  return Boolean(paramName) && params.some((param) => param.paramName === paramName && param.binding === "listing.link");
};

/**
 * The template's URL buttons whose address is FIXED — the same page on every send. A button whose
 * address ends in a variable is not one, whatever that variable is bound to: it follows the send.
 */
export const fixedUrlButtons = (template: Pick<TemplateView, "buttons" | "params">) =>
  (template.buttons ?? []).filter((button): button is Extract<typeof button, { type: "URL" }> => button.type === "URL" && !urlButtonParamName(button));

/** The dialog OFFERS a listing: also for any image header, whose picture a listing can provide. */
export const templateOffersListing = (template: Pick<TemplateView, "params" | "headerMediaSource" | "headerFormat">): boolean =>
  templateWantsListing(template) || template.headerFormat === "IMAGE";

export const listingSourceOf = (listing: PickedListing): ListingBindingSource => ({
  address: listing.address ?? null,
  roomsCount: listing.roomsCount ?? null,
  squareMeter: listing.squareMeter ?? null,
  price: listing.price ?? null,
  description: listing.description ?? null
});

/** A template's listing FIELDS, filled from one listing — what the dialog pre-fills. Never the link. */
export const listingParamValues = (template: Pick<TemplateView, "params">, source: ListingBindingSource): Record<string, string> =>
  Object.fromEntries(
    (template.params ?? [])
      .filter((param) => isListingFieldBinding(param.binding))
      .map((param) => [param.paramName, listingBindingValue(param.binding, source)])
  );

/** What an EMPTY listing field says under it, in every dialog that fills one. */
export const emptyListingFieldNote = (param: WhatsappTemplateParam, opts: { listingPicked: boolean } = { listingPicked: true }): string => {
  const fallback = param.fallbackValue?.trim();
  if (!opts.listingPicked) return fallback ? `ריק — יישלח "${fallback}"` : "כתבו בעצמכם, או בחרו נכס שימלא את זה";
  return fallback ? `המודעה לא מציינת — יישלח "${fallback}"` : "המודעה לא מציינת — מלאו ידנית";
};

const isHttpsUrl = (url: unknown): url is string => typeof url === "string" && /^https:\/\/\S+$/i.test(url.trim());

/**
 * The pictures a listing can head the message with: the cover first, then the gallery, https only
 * (Meta fetches the link itself), each once. With the watermark terms accepted (`clean`), every
 * picture that has a watermark-free version is offered as that version — the same single gate the
 * minisites use (`watermarkFree.ts`).
 */
export const listingImageChoices = (listing: PickedListing, opts: { clean: boolean }): string[] => {
  const clean = opts.clean ? buildWatermarkFreeUrlMap({ watermarkFreeImages: listing.watermarkFreeImages }) : new Map<string, string>();
  const gallery = listing.imageUrls ?? [];
  // The cover is stored at its own path and never gains a clean version; its clean twin is the
  // first gallery picture's (`pickListingDisplayCoverUrl`).
  const cover = listing.coverImageUrl && clean.size && gallery[0] && clean.has(gallery[0]) ? clean.get(gallery[0]) : listing.coverImageUrl;
  const urls = [cover, ...gallery.map((url) => clean.get(url) ?? url)].filter(isHttpsUrl).map((url) => url.trim());
  return Array.from(new Set(urls));
};

/** "הרצל 12, חיפה" — how a picked listing is named on screen. */
export const pickedListingTitle = (listing: PickedListing): string =>
  [listingBindingValue("listing.street", listingSourceOf(listing)), listing.address?.city?.trim()].filter(Boolean).join(", ") ||
  listing.id;
